import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { forwardRef } from "react";
import { BORDER_RADIUS } from "../../utils/constants";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "./chart";
import {
  Area,
  AreaChart as AreaChartLib,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslationApp } from "../../lib/i18next";
import { VALUE_NUMBER } from "../../i18n/keysTranslations";
import { BOX_SHADOW_THEME, INPUT_COLOR_BORDER } from "../../utils/colors";
import { numberDecimal } from "../../utils/numbers";

const AreaChart = forwardRef((props, ref) => {
  const {
    chartData = [],
    chartConfig = {},
    xAxisKey = "label",
    title,
    subtitle,
    height = 320,
    hasTooltip = true,
    hasLegend = true,
    hasCartesianGrid = true,
    embedded = false,
    stacked = false,
    sx = {},
  } = props;

  const { t } = useTranslationApp();
  const chartConfigKeys = Object.keys(chartConfig);
  const useDualAxis =
    !stacked &&
    chartConfigKeys.some((key) => chartConfig[key]?.yAxisId === "right");

  const leftSeriesKey = chartConfigKeys.find(
    (key) => chartConfig[key]?.yAxisId === "left"
  );
  const rightSeriesKey = chartConfigKeys.find(
    (key) => chartConfig[key]?.yAxisId === "right"
  );
  const showAxisCornerLabels = useDualAxis && leftSeriesKey && rightSeriesKey;

  const formatAxisTick = (key, value) => {
    if (chartConfig[key]?.axisValueFormat === "percent") {
      return `${numberDecimal(value)}%`;
    }
    return numberDecimal(value);
  };

  const getValueFormatted = (value, name, payload) => {
    if (chartConfig?.[name]?.valueFormatter) {
      return chartConfig[name].valueFormatter(value, payload);
    }
    return t(VALUE_NUMBER, { value });
  };

  return (
    <Box
      ref={ref}
      sx={{
        ...(embedded
          ? {
              p: 0,
              border: "none",
              boxShadow: "none",
              backgroundColor: "transparent",
            }
          : {
              padding: 3,
              borderRadius: BORDER_RADIUS,
              backgroundColor: "fields.background",
              boxShadow: BOX_SHADOW_THEME,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
            }),
        ...sx,
      }}
    >
      {title && <Typography variant="body1">{title}</Typography>}
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}
      <Box sx={{ width: "100%" }}>
        {showAxisCornerLabels && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              px: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  flexShrink: 0,
                  bgcolor: chartConfig[leftSeriesKey]?.color,
                }}
              />
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "text.secondary", lineHeight: 1 }}
              >
                {chartConfig[leftSeriesKey]?.label}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  flexShrink: 0,
                  bgcolor: chartConfig[rightSeriesKey]?.color,
                }}
              />
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "text.secondary", lineHeight: 1 }}
              >
                {chartConfig[rightSeriesKey]?.label}
              </Typography>
            </Box>
          </Box>
        )}
        <ChartContainer
          config={chartConfig}
          className="aspect-auto w-full"
          style={{ height }}
        >
          <AreaChartLib
            accessibilityLayer
            data={chartData}
            margin={{ top: 12, left: 12, right: 12, bottom: 12 }}
          >
            {hasCartesianGrid && <CartesianGrid vertical={false} />}
            <XAxis
              dataKey={xAxisKey}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval="preserveStartEnd"
            />
            <YAxis
              yAxisId="left"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => numberDecimal(value)}
            />
            {useDualAxis && rightSeriesKey && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => formatAxisTick(rightSeriesKey, value)}
              />
            )}
            {hasTooltip && (
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    formatter={(value, name, item) => (
                      <>
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-[--color-bg]"
                          style={{
                            "--color-bg": `var(--color-${name})`,
                          }}
                        />
                        {chartConfig[name]?.label || name}
                        <div className="ml-auto flex items-baseline gap-0.5 font-mono font-medium tabular-nums text-foreground">
                          {getValueFormatted(value, name, item?.payload)}
                        </div>
                      </>
                    )}
                  />
                }
              />
            )}
            {hasLegend && (
              <ChartLegend content={<ChartLegendContent hideLabel />} />
            )}
            {chartConfigKeys.map((key, index) => (
              <Area
                key={key}
                yAxisId={
                  stacked
                    ? "left"
                    : chartConfig[key]?.yAxisId ||
                      (index === 0 ? "left" : "right")
                }
                stackId={stacked ? chartConfig[key]?.stackId || "a" : undefined}
                dataKey={key}
                type="monotone"
                fill={`var(--color-${key})`}
                fillOpacity={stacked ? 0.55 : 0.25}
                stroke={`var(--color-${key})`}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            ))}
          </AreaChartLib>
        </ChartContainer>
      </Box>
    </Box>
  );
});

AreaChart.displayName = "AreaChart";
export default AreaChart;
